import * as React from "react";
import {
    Autocomplete, TextField, Stack, Typography, Chip,
    Select, MenuItem, Table, TableHead, TableRow, TableCell, TableBody,
    IconButton, Tooltip, Box, Skeleton,
    Avatar
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAppSelector } from "../../../../app/hooks";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { selectAllUsers } from "../../../users/usersSlice";
import { ProcessTeamMember } from "../validationProcessApi";
import PersonIcon from "@mui/icons-material/Person";

type UserOption = {
    userName: string;
    firstname: string;
    surname: string;
    email: string;
    position?: string;
};

type Props = {
    manager: string | null;
    members: ProcessTeamMember[];
    onChange: (state: { manager: string | null; members: ProcessTeamMember[] }) => void;
    skeletonHeight?: number | string;
};

const initials = (u: UserOption) =>
    `${(u.firstname?.[0] ?? "").toUpperCase()}${(u.surname?.[0] ?? "").toUpperCase()}`;

const ROLES = ["M", "C", "O"]; 

export const ValidationTeamEditor: React.FC<Props> = ({
    manager,
    members,
    onChange,
    skeletonHeight = 180,
}) => {
    const allUsers = useAppSelector(selectAllUsers);

    const users: UserOption[] = React.useMemo(
        () =>
            (allUsers ?? []).map((u: any) => ({
                userName: String(u.userName),
                firstname: u.firstName ?? u.firstname ?? "",
                surname: u.lastName ?? u.surname ?? "",
                email: u.email ?? "",
                position: u.position,
            })),
        [allUsers]
    );

    const findUser = (userName?: string | null) => users.find(u => u.userName === userName) || null;
    const selectedUsers = React.useMemo(
        () => members.map(m => users.find(u => u.userName === m.userName)).filter(Boolean) as UserOption[],
        [members, users]
    );

    const setManager = (id: string | null) =>
        onChange({ manager: id, members });

    const setMembers = (next: ProcessTeamMember[]) =>
        onChange({ manager, members: next });

    const handleUsersChange = (_: any, nextUsers: UserOption[]) => {
        const currentRoleByUser = new Map(members.map(m => [m.userName, m.role]));

        const next: ProcessTeamMember[] = nextUsers.map(u => ({
            userName: u.userName,
            role: currentRoleByUser.get(u.userName) ?? ROLES[1],
            isExternal: false
        }));

        setMembers(next);
    };
    const handleRoleChange = (userName: string, role: string) => {
        setMembers(members.map(m => (m.userName === userName ? { ...m, role } : m)));
    };

    const handleRemove = (userName: string) => {
        setMembers(members.filter(m => m.userName !== userName));
    };

    return (
        <Stack spacing={2}>
            <LoadUsers component={<Skeleton variant="rectangular" height={skeletonHeight} />}>
                {/* Manager procesu */}
                <Autocomplete<UserOption, false, false, false>
                    options={users}
                    value={findUser(manager)}
                    onChange={(_, u) => setManager(u?.userName ?? null)}
                    isOptionEqualToValue={(o, v) => o.userName === v.userName}
                    getOptionLabel={(u) => (u ? `${u.firstname} ${u.surname} (${u.email})` : "")}
                    renderOption={(props, u) => (
                        <li {...props} key={u.userName}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar
                                    sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }}
                                    src={(u as any).avatarUrl}
                                >
                                    {initials(u) || <PersonIcon sx={{ fontSize: 16 }} />}
                                </Avatar>
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {u.firstname} {u.surname}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {u.email}{u.position ? ` · ${u.position}` : ""}
                                    </Typography>
                                </Box>
                            </Stack>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Manager procesu" placeholder="Wybierz osobę" />
                    )}
                />

                {/* Członkowie zespołu */}
                <Autocomplete<UserOption, true, false, false>
                    multiple
                    options={users}
                    value={selectedUsers}
                    onChange={handleUsersChange}
                    isOptionEqualToValue={(o, v) => o.userName === v.userName}
                    getOptionLabel={(u) => `${u.firstname} ${u.surname} (${u.email})`}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((u, i) => (
                            <Chip {...getTagProps({ index: i })} key={u.userName} label={`${u.firstname} ${u.surname}`} size="small" />
                        ))
                    }
                    renderOption={(props, u) => (
                        <li {...props} key={u.userName}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar
                                    sx={{ width: 28, height: 28, fontSize: 12 }} 
                                    src={(u as any).avatarUrl }
                                >
                                    {initials(u) || <PersonIcon fontSize="small" sx={{ fontSize: 16 }} />}
                                </Avatar>
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {u.firstname} {u.surname}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {u.email}{u.position ? ` · ${u.position}` : ""}
                                    </Typography>
                                </Box>
                            </Stack>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Członkowie zespołu" placeholder="Dodaj osoby" />
                    )}
                />

                {!!members.length && (
                    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                        <Table size="small" >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Osoba</TableCell>
                                    <TableCell>Rola</TableCell>
                                    <TableCell align="right">Akcje</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.map((m) => {
                                    const u = findUser(m.userName);
                                    return (
                                        <TableRow key={m.userName} hover>
                                            <TableCell>
                                                <Stack>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {u ? `${u.firstname} ${u.surname}` : m.userName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {u?.email}{u?.position ? ` · ${u.position}` : ""}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ p: 0.5 }}>
                                                <Select
                                                    size="small"
                                                    fullWidth
                                                    value={m.role}
                                                    onChange={(e) => handleRoleChange(m.userName, e.target.value as string)}
                                                >
                                                    <MenuItem value="M">Manager</MenuItem>
                                                    <MenuItem value="C">Collaborator</MenuItem>
                                                    <MenuItem value="O">Observer</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell align="right" sx={{ pr: 1 }}>
                                                <Tooltip title="Usuń z zespołu">
                                                    <IconButton size="small" onClick={() => handleRemove(m.userName)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </LoadUsers>
        </Stack>
    );
};
